import { MAP_ACTION_BATCH_SIZE } from "@/config/constants";
import { type TileRepositoryLayerCount } from "@/game/TileRepository";
import useEmitter, {
	EmitterEvents,
	type MapChangePayload,
} from "@/hooks/useEmitter";
import useTileRepository from "@/hooks/useTileRepository";
import { type Timestamp, type Tuple } from "@/types";
import {
	type TileEntityParams,
	type TileId,
	type TileIndex,
} from "@/types/tile";

type useMapActionsResponse = {
	init: () => void;
	getPayload: () => MapMutationsPayload;
	resetPayload: (expectedPayload?: MapMutationsPayload) => void;
	isActionQueueEmpty: () => boolean;
};

type MapMutationActionTile = {
	tileIndex: TileIndex;
	tileIds: Tuple<TileId, TileRepositoryLayerCount>;
};

type MapMutationActionParams = Record<keyof TileEntityParams, number>;

export type MapMutationAction = {
	tile: MapMutationActionTile;
	params: MapMutationActionParams;
	timestamp: Timestamp;
};

type MapMutationsPayload = {
	actions: Array<MapMutationAction>;
};

const actions = Array<MapMutationAction>();

const addAction = (
	tile: MapMutationActionTile,
	params: MapMutationActionParams,
): void => {
	actions.push({
		tile,
		params,
		timestamp: Date.now(),
	});
};

const getActions = (): Array<MapMutationAction> =>
	actions.splice(0, MAP_ACTION_BATCH_SIZE || actions.length);

let payloadInProgress: MapMutationsPayload | null = null;

const getPayload = (): MapMutationsPayload => {
	if (payloadInProgress !== null) return payloadInProgress;

	const nextActions = getActions();
	// Nothing has been reserved for sending. Do not cache an empty batch.
	if (nextActions.length === 0) return { actions: [] };

	return (payloadInProgress = { actions: nextActions });
};

const resetPayload = (expectedPayload?: MapMutationsPayload): void => {
	// An acknowledgement belongs to the exact snapshot that was sent.
	// Keep the no-argument form compatible with existing callers.
	if (expectedPayload !== undefined && expectedPayload !== payloadInProgress)
		return;

	payloadInProgress = null;
};

// Reports only actions waiting for a batch, not an unacknowledged payload.
const isActionQueueEmpty = (): boolean => actions.length === 0;

const init = (): void => {
	const emitter = useEmitter();
	emitter.off(EmitterEvents.MapChange);
	emitter.on(
		EmitterEvents.MapChange,
		({ tileIndex }: MapChangePayload): void => {
			const tilerepo = useTileRepository();
			const tileIds = tilerepo.getTileIdsByTileIndex(tileIndex);
			if (!tileIds)
				throw new Error(
					`Tile IDs not found for tile index: ${tileIndex}`,
				);

			const tileParams = tilerepo.getTileParamsByTileIndex(tileIndex);
			if (!tileParams)
				throw new Error(
					`Tile parameters not found for tile index: ${tileIndex}`,
				);

			addAction({ tileIndex, tileIds }, tileParams);
		},
	);
};

const useMapActions = (): useMapActionsResponse => ({
	init,
	getPayload,
	resetPayload,
	isActionQueueEmpty,
});

export default useMapActions;
